/**
 * Microsoft / Office fonts → free twins.
 *
 * Publisher files almost always name fonts that ship with Windows and Office (Calibri, Cambria, Arial, Georgia, Book
 * Antiqua…). A browser on a PC that has them shows the real thing; everywhere else the browser would silently fall back to
 * its default face, with different letter widths — and the imported layout drifts (extra line breaks, overflowing boxes).
 *
 * The fix is a *twin*: an open font served from Google Fonts that is registered under the Microsoft name (fontLoader.js
 * fetches the twin's @font-face rules and renames the family), so every text box, inline span, table and export that says
 * `font-family: 'Calibri'` keeps working without touching the document. `metric: true` twins share the exact letter widths
 * of the original (same line breaks, same box fit); the others are the closest look-alike available.
 *
 * The LibreOffice runtime carries the same idea for previews / PDFs (lo_runtime.FONT_TWINS + fontconfig aliases).
 */

const T = (name, twin, metric = false, faces = null) => ({ name, twin, metric, faces });

/**
 * Selawik — Microsoft's own open (OFL 1.1) stand-in for Segoe UI, metric-compatible (same advance widths) but not on
 * Google Fonts: the files ship with the app under public/fonts/selawik (LICENSE.txt beside them). A Publisher file set in
 * Segoe UI keeps its line breaks off Windows; Publisher's hanging "Leader: / People:" blocks built with tabs and runs of
 * spaces land where they did. Selawik has no italic — the browser slants the upright face (same widths).
 * Each face: { url, weight }; `font-style` is always normal.
 */
const SELAWIK = '/fonts/selawik';
const selawik = (...files) => files.map(([file, weight]) => ({ url: `${SELAWIK}/${file}.woff2`, weight }));

export const FONT_TWINS = Object.fromEntries([
  // metric-compatible: identical advance widths
  T('Segoe UI', 'Selawik', true, selawik(['selawk', 400], ['selawkb', 700], ['selawkl', 300], ['selawksl', 350], ['selawksb', 600])),
  T('Segoe UI Light', 'Selawik Light', true, selawik(['selawkl', 400], ['selawkb', 700])),
  T('Segoe UI Semilight', 'Selawik Semilight', true, selawik(['selawksl', 400], ['selawkb', 700])),
  T('Segoe UI Semibold', 'Selawik Semibold', true, selawik(['selawksb', 400], ['selawkb', 700])),
  T('Arial', 'Arimo', true),
  T('Helvetica', 'Arimo', true),
  T('Microsoft Sans Serif', 'Arimo', true),
  T('Times New Roman', 'Tinos', true),
  T('Times', 'Tinos', true),
  T('Courier New', 'Cousine', true),
  T('Courier', 'Cousine', true),
  T('Calibri', 'Carlito', true),
  T('Cambria', 'Caladea', true),
  T('Georgia', 'Gelasio', true),
  // closest open look-alikes
  T('Arial Black', 'Archivo Black'),
  T('Arial Narrow', 'Archivo Narrow'),
  T('Baskerville Old Face', 'Libre Baskerville'),
  T('Bodoni MT', 'Bodoni Moda'),
  T('Book Antiqua', 'Crimson Text'),
  T('Bookman Old Style', 'Libre Baskerville'),
  T('Brush Script MT', 'Caveat Brush'),
  T('Candara', 'Nunito Sans'),
  T('Century Gothic', 'Questrial'),
  T('Comic Sans MS', 'Comic Neue'),
  T('Consolas', 'Inconsolata'),
  T('Constantia', 'Spectral'),
  T('Copperplate Gothic', 'Cinzel'),
  T('Corbel', 'Lato'),
  T('Franklin Gothic', 'Libre Franklin'),
  T('Franklin Gothic Book', 'Libre Franklin'),
  T('Franklin Gothic Medium', 'Libre Franklin'),
  T('Franklin Gothic Demi', 'Libre Franklin'),
  T('Franklin Gothic Heavy', 'Libre Franklin'),
  T('Franklin Gothic Medium Cond', 'Libre Franklin'),
  T('Franklin Gothic Demi Cond', 'Libre Franklin'),
  T('Garamond', 'EB Garamond'),
  T('Gill Sans', 'Cabin'),
  T('Gill Sans MT', 'Cabin'),
  T('Goudy Old Style', 'Sorts Mill Goudy'),
  T('Impact', 'Anton'),
  T('Lucida Console', 'Source Code Pro'),
  T('Lucida Handwriting', 'Dancing Script'),
  T('Lucida Sans Unicode', 'Lato'),
  T('Monotype Corsiva', 'Pinyon Script'),
  T('Palatino', 'Crimson Text'),
  T('Palatino Linotype', 'Crimson Text'),
  T('Rockwell', 'Roboto Slab'),
  T('Tahoma', 'Open Sans'),
  T('Trebuchet MS', 'Fira Sans'),
  T('Tw Cen MT', 'Jost'),
  T('Verdana', 'Noto Sans'),
].map((t) => [t.name.toLowerCase(), t]));

const clean = (family) => String(family || '').split(',')[0].replace(/["']/g, '').trim();

/** { name, twin, metric } for a Microsoft family name (any case), else null. */
export const twinOf = (family) => FONT_TWINS[clean(family).toLowerCase()] || null;

/** The Microsoft names, A–Z — the font picker's "Office" tab. */
export const OFFICE_FONTS = Object.values(FONT_TWINS).map((t) => t.name).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

/** One line for the import panel: "Calibri → Carlito (same widths)". */
export const describeTwin = (t) => `${t.name} → ${t.twin}${t.metric ? ' (same letter widths)' : ''}`;
